import type { UseQueryOptions } from '@tanstack/react-query'
import { useQueries } from '@tanstack/react-query'
import { useMemo } from 'react'

import type { RuleRef } from '../services/journey-service'
import type { RuleId } from '../services/validation-rules-service'
import { getValidationRuleById } from '../services/validation-rules-service'
import { selectCurrentValidationRules } from '../store/selectors/runtime'

import { useAppStoreSelector } from './useAppStoreSelector'

type Response = Awaited<ReturnType<typeof getValidationRuleById>>

export const getValidationRuleByIdQueryKey = (
  id: RuleId | undefined | null
) => ['validationRule', id]

// New hook for multiple validation rules
export const useMultipleValidationRulesQuery = (
  ruleRef: RuleRef | undefined,
  options?: Omit<UseQueryOptions<Response>, 'queryKey' | 'queryFn'>
) => {
  // Normalize the input to always be an array of IDs
  const normalizedIds = useMemo(() => {
    if (!ruleRef) return []

    if (typeof ruleRef === 'string') {
      return [ruleRef]
    }

    // If it's RuleRef, extract the IDs from the RuleRef
    return Object.values(ruleRef).flatMap((id) => (id ? [id] : []))
  }, [ruleRef])

  return useQueries({
    queries: normalizedIds.map((id) => ({
      queryKey: getValidationRuleByIdQueryKey(id),
      queryFn: (context) => getValidationRuleById(id, context.signal),
      ...options
    }))
  })
}

/**
 * Custom hook to fetch validation rules for a specific block.
 * @param params - Parameters for the hook
 * @returns An object containing loading state and fetched validation rules
 */
export const useValidationRules = (params: {
  blockId: string | undefined
  isQueryEnabled: boolean
}): {
  isLoadingValidationRules: boolean
  currentValidationRules: Record<'root' | string, Response>
} => {
  // get current validation rule references for block
  const currentValidationRuleRefs = useAppStoreSelector((state) =>
    selectCurrentValidationRules(state, params.blockId)
  )

  // Fetch validation rules when currentValidationRuleRefs is available
  const validationRulesQueries = useMultipleValidationRulesQuery(
    currentValidationRuleRefs,
    {
      enabled: params.isQueryEnabled && !!currentValidationRuleRefs
    }
  )

  // Check if validation rules are still loading
  const isLoadingValidationRules =
    !!currentValidationRuleRefs &&
    validationRulesQueries.some((query) => query.isLoading)

  // Extract the validation rules data
  const currentValidationRules = validationRulesQueries
    .filter((query) => query.isSuccess)
    .map((query) => query.data)
    .reduce(
      (acc, rule) => {
        // if currentValidationRuleRefs was a string, add it as 'root'
        if (typeof currentValidationRuleRefs === 'string') {
          acc['root'] = rule

          return acc
        }

        // if currentValidationRuleRefs was a RuleRef, find the corresponding field name and use it as key
        if (typeof currentValidationRuleRefs === 'object') {
          const fieldName = Object.keys(currentValidationRuleRefs).find(
            (key) => currentValidationRuleRefs[key] === rule._id
          )

          if (fieldName) {
            acc[fieldName] = rule
          }
        }

        return acc
      },
      {} as Record<'root' | string, Response>
    )

  return {
    isLoadingValidationRules,
    currentValidationRules
  }
}
